import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from '../../../services/toast.service';
import { ApiService } from '../../../services/api.service';
import { Order } from '../../../orders/models/order.models';
import { unwrapOrderFromApiResponse } from '../../../shared/utils/order-api.util';
import {
  buildTapToPayNativeParams,
  cleanTapToPayAmount,
  confirmTapToPayPaymentMode,
  resolveInternalOrderId,
  tapToPayOrderIdsMatch,
} from '../../../shared/utils/tap-to-pay-order-params.util';
import {
  TapToPayAndroidBridgeService,
  TapToPayAndroidPaymentDetail,
} from '../../../services/tap-to-pay-android-bridge.service';
import { TapToPayAndroidNativeService } from '../../../services/tap-to-pay-android-native.service';
import { getActiveUserAuthToken } from '../../../shared/utils/auth-headers.util';

/**
 * Tap to Pay for order-details-modal: launch native payment and handle Android bridge results.
 * No status overlay, polling, or tap_to_pay_status tracking.
 */
@Injectable()
export class OrderDetailsTapToPayService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly paymentSuccess$ = new Subject<string>();
  readonly paymentFailed$ = new Subject<{ orderId: string; message: string }>();

  private isGoingToPayment = false;
  private paymentOrderId: string | null = null;
  private processedTapToPaySuccessOrderIds = new Set<string>();
  private tapToPayFailureDedupAttempt = 0;
  private tapToPayFailureToastKeys = new Set<string>();

  private lastOrder: Order | null = null;
  private lastPaymentPhoneNumber = '';
  private lastApiOrderData: any = null;

  constructor(
    private readonly apiService: ApiService,
    private readonly messageService: ToastService,
    private readonly tapToPayAndroidBridge: TapToPayAndroidBridgeService,
    private readonly tapToPayAndroidNative: TapToPayAndroidNativeService
  ) {
    this.tapToPayAndroidBridge.nativePaymentResults$
      .pipe(takeUntil(this.destroy$))
      .subscribe(detail => {
        if (detail.status === 'success') {
          this.onAndroidTapToPayPaymentSuccess(detail);
        } else if (detail.status === 'failed') {
          this.onAndroidTapToPayPaymentFailed(detail);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resetPaymentTracking();
  }

  reset(): void {
    this.resetPaymentTracking();
    this.lastOrder = null;
    this.lastPaymentPhoneNumber = '';
    this.lastApiOrderData = null;
    this.tapToPayFailureToastKeys.clear();
  }

  openTapToPayApp(order: Order, paymentPhoneNumber: string, apiOrderData: any): void {
    this.lastOrder = order;
    this.lastPaymentPhoneNumber = paymentPhoneNumber;
    this.lastApiOrderData = apiOrderData;
    this.beginTapToPayPaymentAttempt();

    if (!order) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order information is not available.',
        life: 3000,
      });
      return;
    }

    if (!order.id) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order ID is not available.',
        life: 3000,
      });
      return;
    }

    this.apiService
      .get(`order/${order.id}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orderResponse: any) => {
          const apiOrder = unwrapOrderFromApiResponse(orderResponse) as any;
          this.buildAndOpenDeepLink(order, paymentPhoneNumber, apiOrder);
        },
        error: () => {
          this.openTapToPayAppWithFallback(order, paymentPhoneNumber, apiOrderData);
        },
      });
  }

  private beginTapToPayPaymentAttempt(): void {
    this.tapToPayFailureDedupAttempt += 1;
    this.tapToPayFailureToastKeys.clear();
  }

  private claimPaymentFailureToast(
    data: TapToPayAndroidPaymentDetail | Record<string, any>,
    orderId: string
  ): boolean {
    const paymentId =
      (data as Record<string, any>)['payment_intent_id'] ||
      (data as Record<string, any>)['order_id'] ||
      (data as Record<string, any>)['orderId'] ||
      orderId;
    const baseKey = paymentId ? `${paymentId}-failed` : `${orderId || 'any'}-failed`;
    const paymentKey = `${this.tapToPayFailureDedupAttempt}:${baseKey}`;
    if (this.tapToPayFailureToastKeys.has(paymentKey)) {
      return false;
    }
    this.tapToPayFailureToastKeys.add(paymentKey);
    return true;
  }

  private showPaymentFailedToast(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Payment Failed',
      detail: message || 'Payment could not be processed. Please try again.',
      life: 5000,
    });
  }

  private buildTapToPayParams(
    order: Order,
    paymentPhoneNumber: string,
    apiOrder: Record<string, any> | null
  ): URLSearchParams {
    const api = apiOrder ?? {};
    const authToken = getActiveUserAuthToken();

    return buildTapToPayNativeParams({
      apiOrder: api,
      fallbackOrderId: order.id || order.orderId,
      source: 'saas',
      amount: cleanTapToPayAmount(order.price || api['final_price']),
      customerId: String(api['customer_id'] ?? api['customerId'] ?? order.customerId ?? ''),
      locationId: String(api['location_id'] ?? api['locationId'] ?? ''),
      adminUserId: String(api['user_id'] ?? ''),
      washType: String(api['wash_type'] ?? order.washTypeValue ?? ''),
      packageId: String(api['package_id'] ?? ''),
      vehicleId: String(api['vehicle_id'] ?? order.vehicleId ?? ''),
      stripeAccountId: String(api['stripe_account_id'] ?? ''),
      phoneNumber: paymentPhoneNumber || order.phone || String(api['customer_contact'] ?? ''),
      email: order.email || String(api['customer_email'] ?? ''),
      authToken,
      publicOrderId: String(order.orderId || api['order_id'] || api['orderId'] || ''),
    });
  }

  private buildAndOpenDeepLink(order: Order, paymentPhoneNumber: string, apiOrder: any): void {
    const orderRecord = (apiOrder ?? {}) as Record<string, any>;
    const params = this.buildTapToPayParams(order, paymentPhoneNumber, orderRecord);
    const internalOrderId = resolveInternalOrderId(orderRecord, order.id);

    this.isGoingToPayment = true;
    this.paymentOrderId = internalOrderId || params.get('public_order_id') || null;
    const sessionKey = internalOrderId || String(order.id || '').trim();
    if (sessionKey) {
      this.processedTapToPaySuccessOrderIds.delete(sessionKey);
    }

    this.sendTapToPayToNativeApp(params);
  }

  private sendTapToPayToNativeApp(params: URLSearchParams): void {
    if (!this.tapToPayAndroidNative.isAvailable) {
      console.warn('[TapToPay] Native Android shell required (TapToPayAndroid bridge missing).');
      return;
    }
    this.tapToPayAndroidNative.startPaymentFromParams(params);
  }

  private openTapToPayAppWithFallback(
    order: Order,
    paymentPhoneNumber: string,
    apiOrderData: any
  ): void {
    const apiRecord = (apiOrderData ?? {}) as Record<string, any>;
    const params = this.buildTapToPayParams(order, paymentPhoneNumber, apiRecord);
    const internalOrderId = resolveInternalOrderId(apiRecord, order.id);

    this.isGoingToPayment = true;
    this.paymentOrderId = internalOrderId || params.get('public_order_id') || null;
    const sessionKeyFb = internalOrderId || String(order.id || '').trim();
    if (sessionKeyFb) {
      this.processedTapToPaySuccessOrderIds.delete(sessionKeyFb);
    }

    this.sendTapToPayToNativeApp(params);
  }

  private onAndroidTapToPayPaymentSuccess(detail: TapToPayAndroidPaymentDetail): void {
    if (!this.isGoingToPayment || detail.status !== 'success') {
      return;
    }
    const incoming = (detail.order_id ?? detail.internal_id ?? '').replace(/^#+/, '').trim();
    if (!incoming) {
      return;
    }
    if (
      !tapToPayOrderIdsMatch(incoming, [
        this.paymentOrderId,
        this.lastOrder?.id,
        this.lastOrder?.orderId,
      ])
    ) {
      return;
    }

    const emitOrderId = (this.paymentOrderId ?? '').replace(/^#+/, '').trim() || incoming;
    const successKey = (this.lastOrder?.id && String(this.lastOrder.id).trim()) || emitOrderId;
    if (successKey && this.processedTapToPaySuccessOrderIds.has(successKey)) {
      return;
    }

    // Native success is authoritative; confirm card_present on the backend first (same as notification modal).
    // Do not pre-check payment_status here — the order may still be pending until confirm-payment-mode runs.
    this.completeTapToPaySuccess(emitOrderId);
  }

  private onAndroidTapToPayPaymentFailed(detail: TapToPayAndroidPaymentDetail): void {
    if (!this.isGoingToPayment || detail.status !== 'failed') {
      return;
    }
    const incoming = (detail.order_id ?? '').replace(/^#+/, '').trim();
    const incomingInternal = (detail.internal_id ?? '').replace(/^#+/, '').trim();
    const hasIncoming = !!(incoming || incomingInternal);
    const matched =
      !hasIncoming ||
      tapToPayOrderIdsMatch(incoming || incomingInternal, [
        this.paymentOrderId,
        this.lastOrder?.id,
        this.lastOrder?.orderId,
      ]);
    if (!matched) {
      return;
    }

    const orderIdForUi =
      incoming ||
      incomingInternal ||
      this.paymentOrderId ||
      this.lastOrder?.id ||
      this.lastOrder?.orderId ||
      '';
    const successKey =
      (this.lastOrder?.id && String(this.lastOrder.id).trim()) ||
      String(orderIdForUi).replace(/^#+/, '').trim();
    if (successKey && this.processedTapToPaySuccessOrderIds.has(successKey)) {
      return;
    }

    const msg = (detail.message ?? '').trim() || 'Payment could not be completed.';

    this.resetPaymentTracking();

    if (this.claimPaymentFailureToast(detail, String(orderIdForUi))) {
      this.showPaymentFailedToast(msg);
    }
    this.paymentFailed$.next({ orderId: String(orderIdForUi), message: msg });
  }

  private completeTapToPaySuccess(sourceOrderId?: string): void {
    if (!this.isGoingToPayment) {
      return;
    }

    const idKey =
      (this.lastOrder?.id && String(this.lastOrder.id).trim()) ||
      (this.paymentOrderId && String(this.paymentOrderId).trim()) ||
      (sourceOrderId && String(sourceOrderId).trim()) ||
      '';

    if (idKey && this.processedTapToPaySuccessOrderIds.has(idKey)) {
      return;
    }
    if (idKey) {
      this.processedTapToPaySuccessOrderIds.add(idKey);
      setTimeout(() => this.processedTapToPaySuccessOrderIds.delete(idKey), 5 * 60 * 1000);
    }

    this.isGoingToPayment = false;
    this.paymentOrderId = null;

    if (!idKey) {
      return;
    }

    confirmTapToPayPaymentMode(this.apiService, idKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.paymentSuccess$.next(idKey);
        },
        error: () => {
          this.paymentSuccess$.next(idKey);
        },
      });
  }

  private resetPaymentTracking(): void {
    this.isGoingToPayment = false;
    this.paymentOrderId = null;
  }
}
